import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../utils/constants';
import Page from '../components/Page/Page';
import { useThemeContext } from '../context/ThemeContext';
import { getUserRoles } from '../services/userRoles';

// ── Shared style helpers ────────────────────────────────────────────────────
const FONT = "'Lucida Sans', 'Segoe UI', Roboto, sans-serif";
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

// ── Sub-components ──────────────────────────────────────────────────────────
interface SectionHeaderProps {
  title: string;
  accent: string;
  text: string;
}
const SectionHeader = ({ title, accent, text }: SectionHeaderProps) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
    <Box sx={{ width: 4, height: 28, backgroundColor: accent, borderRadius: 1, flexShrink: 0 }} />
    <Typography
      variant="h6"
      sx={{
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: text,
      }}
    >
      {title}
    </Typography>
  </Stack>
);

// ── Main component ──────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();

  const { colors } = useThemeContext();

  // Local state for user roles
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Replace 'your-query' with the actual query param if needed
    getUserRoles('')
      .then((data) => {
        setUserData(data);
        console.log('LandingPage getUserRoles data:', data);
      })
      .finally(() => setLoading(false));
  }, []);

  // Use the correct path for the API response
  // Defensive: support both userData.data and userData root (for flexibility)
  const importerModuleView = !!(
    userData?.data?.entityPermission?.modulePermissions?.IMPORTER_MODULE?.view ??
    userData?.entityPermission?.modulePermissions?.IMPORTER_MODULE?.view
  );

  const handleModuleClick = (module: { path: string; external: boolean }) => {
    if (module.external) {
      window.location.href = module.path;
    } else {
      navigate(module.path);
    }
  };

  // Conditionally include Importer Module
  const modules = [
    {
      id: 'direct-to-customer',
      title: 'Direct to Customer',
      description: 'Manage direct customer sales, vehicle assignments, and delivery coordination.',
      icon: 'mdi:account-car',
      path: PATHS.directToCustomer,
      external: false,
      stat: 'Sales & Delivery',
    },
    ...(importerModuleView
      ? [
          {
            id: 'importer',
            title: 'Importer Module',
            description: 'Handle vehicle imports, customs clearance, and international logistics.',
            icon: 'mdi:ship-wheel',
            path: '/importer',
            external: true,
            stat: 'Import & Logistics',
          },
        ]
      : []),
    {
      id: 'inventory-management',
      title: 'Inventory Management',
      description: 'Track and manage vehicle inventory, stock levels, and warehouse operations.',
      icon: 'mdi:warehouse',
      path: PATHS.inventory,
      external: false,
      stat: 'Stock & Warehouse',
    },
  ];

  const quickLinks = [
    { label: 'All Vehicles', path: PATHS.vehicles, icon: 'mdi:car-multiple' },
    { label: 'Inventory', path: PATHS.inventory, icon: 'mdi:warehouse' },
    { label: 'Assignments', path: PATHS.assignment, icon: 'mdi:clipboard-check' },
  ];

  if (loading) return <div>Loading user roles...</div>;

  // Debug: show the raw API response
  // Remove this after confirming structure
  if (!userData)
    return (
      <div>
        No user data found.
        <br />
        API Response: <pre>{JSON.stringify(userData, null, 2)}</pre>
      </div>
    );

  return (
    <Page
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.bg,
        fontFamily: FONT,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Sticky breadcrumb / top bar ─────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: colors.bgCard,
          borderBottom: `1.5px solid ${colors.border}`,
          px: 4,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon icon="mdi:home-outline" style={{ fontSize: '14px', color: colors.textSecondary }} />
          <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, fontFamily: FONT }}>
            V360 Platform
          </Typography>
          <Icon icon="mdi:chevron-right" style={{ fontSize: '12px', color: colors.textSecondary }} />
          <Typography sx={{ fontSize: '0.75rem', color: colors.accent, fontFamily: FONT, fontWeight: 700 }}>
            Dashboard
          </Typography>
        </Stack>
      </Box>

      {/* ── Scrollable content ──────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 } }}>
        <Stack spacing={4}>
          {/* ── Page header ──────────────────────────────────────────────── */}
          <Box>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                  <Icon
                    icon="mdi:view-dashboard"
                    style={{ fontSize: '28px', color: colors.accent, transition: TRANSITION }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: FONT,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: colors.text,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    V360 PLATFORM
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.82rem',
                    color: colors.textSecondary,
                    pl: '44px',
                  }}
                >
                  Select a module to manage your vehicle operations
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* ── Module cards ─────────────────────────────────────────────── */}
          <Box>
            <SectionHeader title="Modules" accent={colors.accent} text={colors.text} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2.5,
              }}
            >
              {modules.map((module) => (
                <Box
                  key={module.id}
                  onClick={() => handleModuleClick(module)}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    backgroundColor: colors.bgCard,
                    border: `1.5px solid ${colors.border}`,
                    boxShadow: colors.shadow,
                    transition: TRANSITION,
                    minHeight: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: colors.shadowHover,
                      borderColor: colors.accent,
                      '& .module-icon': { transform: 'scale(1.1) rotate(5deg)' },
                      '& .module-arrow': { transform: 'translateX(4px)' },
                    },
                  }}
                >
                  {/* Icon + stat label */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Icon
                      icon={module.icon}
                      className="module-icon"
                      style={{
                        fontSize: '28px',
                        color: colors.accent,
                        transition: TRANSITION,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.68rem',
                        color: colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        border: `1px solid ${colors.border}`,
                        px: 1,
                        py: 0.25,
                        borderRadius: '4px',
                      }}
                    >
                      {module.stat}
                    </Typography>
                  </Stack>

                  {/* Title + description */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: colors.text,
                        mb: 1,
                      }}
                    >
                      {module.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.8rem',
                        color: colors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {module.description}
                    </Typography>
                  </Box>

                  {/* Access link */}
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: colors.accent,
                      }}
                    >
                      Access Module
                    </Typography>
                    <Icon
                      icon="mdi:arrow-right"
                      className="module-arrow"
                      style={{ fontSize: '16px', color: colors.accent, transition: TRANSITION }}
                    />
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Quick access ─────────────────────────────────────────────── */}
          <Box>
            <SectionHeader title="Quick Access" accent={colors.accent} text={colors.text} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                gap: 1.5,
              }}
            >
              {quickLinks.map((link) => (
                <Box
                  key={link.label}
                  onClick={() => navigate(link.path)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: '8px',
                    backgroundColor: colors.bgCard,
                    border: `1.5px solid ${colors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    transition: TRANSITION,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: colors.accent,
                      boxShadow: colors.shadowHover,
                      '& .link-icon': { transform: 'scale(1.1) rotate(5deg)' },
                    },
                  }}
                >
                  <Icon
                    icon={link.icon}
                    className="link-icon"
                    style={{ fontSize: '24px', color: colors.accent, transition: TRANSITION }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: colors.text,
                      textAlign: 'center',
                    }}
                  >
                    {link.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Info banner ──────────────────────────────────────────────── */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              backgroundColor: colors.bgCard,
              border: `1.5px solid ${colors.border}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Icon
                icon="mdi:information-outline"
                style={{ fontSize: '20px', color: colors.accent, marginTop: '2px', flexShrink: 0 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text,
                    mb: 0.5,
                  }}
                >
                  Welcome to V360
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.8rem',
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                  }}
                >
                  V360 provides comprehensive tools for managing vehicle operations, inventory tracking, and customer
                  relationships. Select a module above to get started.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Page>
  );
};

export default LandingPage;
